// src/component/viewDetails.jsx

import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import ApiURl from "../controllers/Api";
import imgLocation from "../controllers/imagePath";

/* ============================================================
   SL GOLD THEME
   ============================================================ */

const PAGE_BG = "var(--user-bg, #E8D9D5)";
const MAROON = "var(--user-maroon, #2B0000)";
const GOLD = "var(--user-gold, #D4AF37)";

const CREAM = "#FBF7F2";
const SOFT_CREAM = "#F6EFEA";

const TEXT_SOFT = "rgba(43,0,0,.62)";
const TEXT_LIGHT = "rgba(43,0,0,.48)";
const BORDER = "rgba(43,0,0,.13)";
const GOLD_BORDER = "rgba(212,175,55,.30)";

const ViewDetails = () => {
  const { order_id } = useParams();
  const navigate = useNavigate();

  const [orderDetails, setOrderDetails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /* ============================================================
     FALLBACK IMAGE
     ============================================================ */

  const FALLBACK_IMG =
    "data:image/svg+xml;utf8," +
    encodeURIComponent(`
      <svg xmlns="http://www.w3.org/2000/svg" width="300" height="300">
        <rect width="100%" height="100%" fill="#F6EFEA"/>
        <text
          x="50%"
          y="50%"
          dominant-baseline="middle"
          text-anchor="middle"
          font-family="Arial"
          font-size="18"
          fill="#6b5555"
        >
          No Image
        </text>
      </svg>
    `);

  /* ============================================================
     ORDER DATA
     ============================================================ */

  const orderInfo = useMemo(() => {
    return Array.isArray(orderDetails) &&
      orderDetails.length > 0
      ? orderDetails[0]
      : null;
  }, [orderDetails]);

  const products = useMemo(() => {
    return Array.isArray(orderInfo?.products)
      ? orderInfo.products
      : [];
  }, [orderInfo]);

  /* ============================================================
     CUSTOMER
     ============================================================ */

  const customerDisplayName = useMemo(() => {
    if (!orderInfo) return "N/A";

    return (
      orderInfo.shipping_full_name ||
      `${orderInfo.first_name || ""} ${
        orderInfo.last_name || ""
      }`.trim() ||
      "N/A"
    );
  }, [orderInfo]);

  const customerDisplayPhone = useMemo(() => {
    if (!orderInfo) return "N/A";

    return (
      orderInfo.shipping_phone ||
      orderInfo.phone ||
      "N/A"
    );
  }, [orderInfo]);

  /* ============================================================
     FORMATTERS
     ============================================================ */

  const formatCurrency = (value) =>
    `₹${Number(value || 0).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";

    try {
      const date = new Date(dateString);

      return date.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return dateString;
    }
  };

  /* ============================================================
     IMAGE HANDLING
     ============================================================ */

  const extractImagePath = (imgPath) => {
    if (!imgPath) return null;

    if (typeof imgPath === "string") {
      try {
        const parsed = JSON.parse(imgPath);

        if (
          Array.isArray(parsed) &&
          parsed.length > 0
        ) {
          return parsed[0];
        }
      } catch {}

      if (imgPath.includes(",")) {
        return imgPath.split(",")[0].trim();
      }

      return imgPath;
    }

    if (
      Array.isArray(imgPath) &&
      imgPath.length > 0
    ) {
      return imgPath[0];
    }

    return null;
  };

  const getImageUrl = (imgPath) => {
    const extractedPath =
      extractImagePath(imgPath);

    if (!extractedPath) {
      return FALLBACK_IMG;
    }

    if (
      extractedPath.startsWith("http://") ||
      extractedPath.startsWith("https://") ||
      extractedPath.startsWith("data:")
    ) {
      return extractedPath;
    }

    const cleanPath =
      extractedPath.replace(/^\/+/, "");

    return `${imgLocation}/${cleanPath}`;
  };

  /* ============================================================
     COUPON
     ============================================================ */

  const resolveCouponDiscount = (
    info,
    subtotal
  ) => {
    if (!info || subtotal <= 0) return 0;

    const couponId = Number(
      info?.coupon_id || 0
    );

    const hasCouponCode =
      info?.coupon_code &&
      String(info.coupon_code).trim() !== "" &&
      info?.coupon_code !== "N/A";

    if (!couponId && !hasCouponCode) {
      return 0;
    }

    const couponType = String(
      info?.coupon_type ||
        info?.discount_type ||
        ""
    ).toLowerCase();

    const fixedCandidates = [
      info?.coupon_value,
      info?.coupon_amount,
      info?.discount_amount,
      info?.discount_value,
      info?.applied_discount,
      info?.flat_discount,
    ];

    const percentageCandidates = [
      info?.coupon_percentage,
      info?.discount_percentage,
      info?.percentage,
    ];

    const fallbackCandidates = [
      info?.coupon_discount,
    ];

    const firstValid = (array) => {
      for (const value of array) {
        const number = Number(value);

        if (
          !Number.isNaN(number) &&
          number > 0
        ) {
          return number;
        }
      }

      return 0;
    };

    const fixedValue =
      firstValid(fixedCandidates);

    const percentageValue =
      firstValid(percentageCandidates);

    const fallbackValue =
      firstValid(fallbackCandidates);

    let discount = 0;

    if (couponType.includes("percent")) {
      discount =
        (subtotal * percentageValue) / 100;
    } else if (
      couponType.includes("fixed") ||
      couponType.includes("flat")
    ) {
      discount =
        fixedValue || fallbackValue;
    } else {
      discount =
        fixedValue ||
        (percentageValue
          ? (subtotal * percentageValue) /
            100
          : fallbackValue);
    }

    return Math.min(
      subtotal,
      Number(discount || 0)
    );
  };

  /* ============================================================
     BULK DISCOUNTS
     ============================================================ */

  const enhancedProducts = useMemo(() => {
    return products.map((item) => {
      const isBulkDiscount =
        item.discount_type === "bulk";

      const paidPrice = Number(
        item.price || 0
      );

      const originalPrice =
        isBulkDiscount
          ? Number(
              item.original_price ||
                paidPrice
            )
          : paidPrice;

      const quantity = Number(
        item.quantity || 0
      );

      const savings = isBulkDiscount
        ? (originalPrice - paidPrice) *
          quantity
        : 0;

      const savingsPercent =
        isBulkDiscount &&
        originalPrice > 0
          ? Math.round(
              ((originalPrice -
                paidPrice) /
                originalPrice) *
                100
            )
          : 0;

      return {
        ...item,
        isBulkDiscount,
        originalPrice,
        paidPrice,
        savings,
        savingsPercent,
        displayPrice: paidPrice,
        displayOriginalPrice:
          isBulkDiscount
            ? originalPrice
            : null,
      };
    });
  }, [products]);

  const hasBulkDiscount = useMemo(
    () =>
      enhancedProducts.some(
        (product) =>
          product.isBulkDiscount
      ),
    [enhancedProducts]
  );

  /* ============================================================
     PRICE CALCULATIONS
     ============================================================ */

  const priceBreakdown = useMemo(() => {
    const empty = {
      subtotal: 0,
      discountAmount: 0,
      discountedSubtotal: 0,

      totalCgst: 0,
      totalSgst: 0,
      totalIgst: 0,
      totalTax: 0,

      grandTotalBeforeDiscount: 0,
      grandTotalAfterDiscount: 0,

      roundedTotal: 0,
      roundingDiff: 0,

      itemWiseBreakdown: [],
    };

    if (
      !orderInfo ||
      !enhancedProducts.length
    ) {
      return empty;
    }

    const safeProducts =
      enhancedProducts.map((item) => {
        const quantity = Number(
          item.quantity || 0
        );

        const price = Number(
          item.paidPrice || 0
        );

        const itemSubtotal =
          price * quantity;

        const cgst = Number(
          item.cgst || 0
        );

        const sgst = Number(
          item.sgst || 0
        );

        const igst = Number(
          item.igst || 0
        );

        const totalTax =
          cgst + sgst + igst;

        const cgstRate =
          itemSubtotal > 0
            ? cgst / itemSubtotal
            : 0;

        const sgstRate =
          itemSubtotal > 0
            ? sgst / itemSubtotal
            : 0;

        const igstRate =
          itemSubtotal > 0
            ? igst / itemSubtotal
            : 0;

        return {
          ...item,

          quantity,
          price,
          itemSubtotal,

          cgst,
          sgst,
          igst,

          totalTax,

          cgstRate,
          sgstRate,
          igstRate,
        };
      });

    const subtotal =
      safeProducts.reduce(
        (sum, item) =>
          sum + item.itemSubtotal,
        0
      );

    const discountAmount =
      resolveCouponDiscount(
        orderInfo,
        subtotal
      );

    const discountedSubtotal =
      Math.max(
        0,
        subtotal - discountAmount
      );

    let totalCgst = 0;
    let totalSgst = 0;
    let totalIgst = 0;

    const itemWiseBreakdown =
      safeProducts.map((item) => {
        const discountShare =
          subtotal > 0
            ? (item.itemSubtotal /
                subtotal) *
              discountAmount
            : 0;

        const taxableAfterDiscount =
          Math.max(
            0,
            item.itemSubtotal -
              discountShare
          );

        const recalculatedCgst =
          taxableAfterDiscount *
          item.cgstRate;

        const recalculatedSgst =
          taxableAfterDiscount *
          item.sgstRate;

        const recalculatedIgst =
          taxableAfterDiscount *
          item.igstRate;

        totalCgst += recalculatedCgst;
        totalSgst += recalculatedSgst;
        totalIgst += recalculatedIgst;

        return {
          name:
            item.name || "Unknown",

          product_id:
            item.product_id,

          size:
            item.size || "N/A",

          paidPrice:
            item.paidPrice,

          quantity:
            item.quantity,

          unitPrice:
            item.paidPrice,

          originalPrice:
            item.originalPrice,

          itemSubtotal: Number(
            item.itemSubtotal.toFixed(2)
          ),

          discountShare: Number(
            discountShare.toFixed(2)
          ),

          taxableAfterDiscount:
            Number(
              taxableAfterDiscount.toFixed(
                2
              )
            ),

          cgstRatePercent: Number(
            (
              item.cgstRate * 100
            ).toFixed(2)
          ),

          sgstRatePercent: Number(
            (
              item.sgstRate * 100
            ).toFixed(2)
          ),

          igstRatePercent: Number(
            (
              item.igstRate * 100
            ).toFixed(2)
          ),

          cgst: Number(
            recalculatedCgst.toFixed(2)
          ),

          sgst: Number(
            recalculatedSgst.toFixed(2)
          ),

          igst: Number(
            recalculatedIgst.toFixed(2)
          ),

          finalLineTotal: Number(
            (
              taxableAfterDiscount +
              recalculatedCgst +
              recalculatedSgst +
              recalculatedIgst
            ).toFixed(2)
          ),

          img_path:
            item.img_path,

          isBulkDiscount:
            item.isBulkDiscount,

          savings:
            item.savings,

          savingsPercent:
            item.savingsPercent,
        };
      });

    const totalTax =
      totalCgst +
      totalSgst +
      totalIgst;

    const grandTotalBeforeDiscount =
      subtotal +
      safeProducts.reduce(
        (sum, item) =>
          sum + item.totalTax,
        0
      );

    const grandTotalAfterDiscount =
      discountedSubtotal + totalTax;

    const roundedTotal = Math.round(
      grandTotalAfterDiscount
    );

    const roundingDiff =
      roundedTotal -
      grandTotalAfterDiscount;

    return {
      subtotal: Number(
        subtotal.toFixed(2)
      ),

      discountAmount: Number(
        discountAmount.toFixed(2)
      ),

      discountedSubtotal: Number(
        discountedSubtotal.toFixed(2)
      ),

      totalCgst: Number(
        totalCgst.toFixed(2)
      ),

      totalSgst: Number(
        totalSgst.toFixed(2)
      ),

      totalIgst: Number(
        totalIgst.toFixed(2)
      ),

      totalTax: Number(
        totalTax.toFixed(2)
      ),

      grandTotalBeforeDiscount:
        Number(
          grandTotalBeforeDiscount.toFixed(
            2
          )
        ),

      grandTotalAfterDiscount:
        Number(
          grandTotalAfterDiscount.toFixed(
            2
          )
        ),

      roundedTotal: Number(
        roundedTotal.toFixed(2)
      ),

      roundingDiff: Number(
        roundingDiff.toFixed(2)
      ),

      itemWiseBreakdown,
    };
  }, [orderInfo, enhancedProducts]);

  const hasCoupon = useMemo(
    () =>
      priceBreakdown.discountAmount > 0,
    [priceBreakdown.discountAmount]
  );

  const finalAmount = useMemo(() => {
    if (!orderInfo) return 0;

    return Number(
      priceBreakdown.roundedTotal || 0
    );
  }, [
    orderInfo,
    priceBreakdown.roundedTotal,
  ]);

  /* ============================================================
     FETCH ORDER
     ============================================================ */

  useEffect(() => {
    const fetchOrderDetails =
      async () => {
        try {
          const user_id =
            localStorage.getItem(
              "user_id"
            );

          if (!user_id) {
            setError(
              "User ID is missing. Please log in."
            );

            setLoading(false);

            return;
          }

          const response =
            await fetch(
              `${ApiURl}/getOrderDetails.php?order_id=${order_id}&user_id=${user_id}`
            );

          const data =
            await response.json();

          if (data.success) {
            setOrderDetails(
              data.data
            );
          } else {
            throw new Error(
              data.message ||
                "No data available"
            );
          }
        } catch (err) {
          setError(err.message);
        } finally {
          setLoading(false);
        }
      };

    fetchOrderDetails();
  }, [order_id]);

  /* ============================================================
     STATUS COMPONENT
     ============================================================ */

  const StatusBadge = () => {
    const status = String(
      orderInfo?.status || ""
    ).toLowerCase();

    let config = {
      text: orderInfo?.status || "Unknown",
      color: MAROON,
      background:
        "rgba(43,0,0,.06)",
      border: BORDER,
      dot: MAROON,
    };

    if (
      status === "completed" ||
      status === "delivered"
    ) {
      config = {
        text: orderInfo.status,
        color: "#326B4F",
        background:
          "rgba(50,107,79,.08)",
        border:
          "rgba(50,107,79,.20)",
        dot: "#477B60",
      };
    }

    if (status === "pending") {
      config = {
        text: orderInfo.status,
        color: "#876510",
        background:
          "rgba(212,175,55,.12)",
        border:
          "rgba(212,175,55,.35)",
        dot: GOLD,
      };
    }

    if (status === "cancelled") {
      config = {
        text: orderInfo.status,
        color: MAROON,
        background:
          "rgba(43,0,0,.06)",
        border:
          "rgba(43,0,0,.18)",
        dot: MAROON,
      };
    }

    if (status === "processing") {
      config = {
        text: orderInfo.status,
        color: MAROON,
        background:
          "rgba(212,175,55,.10)",
        border: GOLD_BORDER,
        dot: GOLD,
      };
    }

    return (
      <span
        className="
          inline-flex
          items-center
          gap-2
          rounded-full
          border
          px-3
          py-1.5
          text-[10px]
          font-semibold
          uppercase
          tracking-[0.12em]
        "
        style={{
          color: config.color,
          backgroundColor:
            config.background,
          borderColor:
            config.border,
        }}
      >
        <span
          className="
            h-[5px]
            w-[5px]
            rounded-full
          "
          style={{
            backgroundColor:
              config.dot,
          }}
        />

        {config.text}
      </span>
    );
  };

  /* ============================================================
     LOADING
     ============================================================ */

  if (loading) {
    return (
      <section
        className="
          flex
          min-h-[70vh]
          items-center
          justify-center
          px-4
          py-16
        "
        style={{
          backgroundColor: PAGE_BG,
        }}
      >
        <div
          className="
            w-full
            max-w-[750px]
            rounded-[22px]
            border
            p-7
            sm:p-10
          "
          style={{
            backgroundColor: CREAM,
            borderColor: GOLD_BORDER,
          }}
        >
          <div className="flex gap-4">
            <div
              className="
                h-14
                w-14
                animate-pulse
                rounded-full
              "
              style={{
                backgroundColor:
                  "rgba(212,175,55,.14)",
              }}
            />

            <div className="flex-1">
              <div
                className="
                  h-5
                  w-44
                  animate-pulse
                  rounded
                "
                style={{
                  backgroundColor:
                    "rgba(43,0,0,.10)",
                }}
              />

              <div
                className="
                  mt-3
                  h-3
                  w-60
                  animate-pulse
                  rounded
                "
                style={{
                  backgroundColor:
                    "rgba(43,0,0,.07)",
                }}
              />
            </div>
          </div>

          <div className="mt-8 space-y-3">
            {[1, 2, 3].map((item) => (
              <div
                key={item}
                className="
                  h-16
                  animate-pulse
                  rounded-[14px]
                "
                style={{
                  backgroundColor:
                    SOFT_CREAM,
                }}
              />
            ))}
          </div>
        </div>
      </section>
    );
  }

  /* ============================================================
     ERROR
     ============================================================ */

  if (error || !orderInfo) {
    return (
      <section
        className="
          flex
          min-h-[70vh]
          items-center
          justify-center
          px-4
          py-16
        "
        style={{
          backgroundColor: PAGE_BG,
        }}
      >
        <div
          className="
            w-full
            max-w-[520px]
            rounded-[22px]
            border
            px-6
            py-10
            text-center
          "
          style={{
            backgroundColor: CREAM,
            borderColor: GOLD_BORDER,
          }}
        >
          <div
            className="
              mx-auto
              flex
              h-14
              w-14
              items-center
              justify-center
              rounded-full
              font-serif
              text-2xl
            "
            style={{
              color: GOLD,
              backgroundColor: MAROON,
            }}
          >
            !
          </div>

          <h2
            className="
              mt-5
              font-serif
              text-[27px]
              font-medium
            "
            style={{
              color: MAROON,
            }}
          >
            {error
              ? "Something went wrong"
              : "No Order Found"}
          </h2>

          <p
            className="
              mt-2
              text-[13px]
            "
            style={{
              color: TEXT_SOFT,
            }}
          >
            {error ||
              "We couldn't find any order data."}
          </p>

          <div
            className="
              mt-6
              flex
              flex-wrap
              justify-center
              gap-2
            "
          >
            {error && (
              <button
                onClick={() =>
                  window.location.reload()
                }
                className="
                  rounded-full
                  px-5
                  py-2.5
                  text-[11px]
                  font-semibold
                  uppercase
                  tracking-[.1em]
                "
                style={{
                  color: GOLD,
                  backgroundColor:
                    MAROON,
                }}
              >
                Try Again
              </button>
            )}

            <button
              onClick={() =>
                navigate(
                  "/order-section"
                )
              }
              className="
                rounded-full
                border
                px-5
                py-2.5
                text-[11px]
                font-semibold
                uppercase
                tracking-[.1em]
              "
              style={{
                color: MAROON,
                borderColor:
                  GOLD_BORDER,
              }}
            >
              Back to Orders
            </button>
          </div>
        </div>
      </section>
    );
  }

  /* ============================================================
     MAIN PAGE
     ============================================================ */

  return (
    <section
      className="
        min-h-screen
        w-full
        px-3
        py-10
        sm:px-6
        sm:py-14
        lg:px-10
        lg:py-16
      "
      style={{
        backgroundColor: PAGE_BG,
      }}
    >
      <div
        className="
          mx-auto
          max-w-[1250px]
        "
      >
        {/* ======================================================
            PAGE HEADER
            ====================================================== */}

        <div
          className="
            mb-8
            text-center
          "
        >
          <div
            className="
              mb-3
              flex
              items-center
              justify-center
              gap-3
            "
          >
            <span
              className="h-px w-8"
              style={{
                backgroundColor:
                  MAROON,
                opacity: 0.5,
              }}
            />

            <span
              className="
                text-[9px]
                font-semibold
                uppercase
                tracking-[.28em]
              "
              style={{
                color: MAROON,
              }}
            >
              SL Gold
            </span>

            <span
              className="h-px w-8"
              style={{
                backgroundColor:
                  MAROON,
                opacity: 0.5,
              }}
            />
          </div>

          <h1
            className="
              font-serif
              text-[34px]
              font-medium
              leading-tight
              tracking-[-.025em]
              sm:text-[40px]
              lg:text-[46px]
            "
            style={{
              color: MAROON,
            }}
          >
            Order Details
          </h1>

          <div
            className="
              mt-3
              flex
              flex-wrap
              items-center
              justify-center
              gap-2
            "
          >
            <span
              className="
                font-serif
                text-[14px]
              "
              style={{
                color: TEXT_SOFT,
              }}
            >
              Order #{orderInfo.order_id}
            </span>

            <span
              style={{
                color: TEXT_LIGHT,
              }}
            >
              •
            </span>

            <span
              className="text-[12px]"
              style={{
                color: TEXT_SOFT,
              }}
            >
              {formatDate(
                orderInfo.date
              )}
            </span>

            <StatusBadge />
          </div>

          <button
            onClick={() =>
              navigate("/order-section")
            }
            className="
              mt-5
              inline-flex
              items-center
              gap-2
              rounded-full
              border
              px-5
              py-2.5
              text-[10px]
              font-semibold
              uppercase
              tracking-[.12em]
            "
            style={{
              color: MAROON,
              borderColor:
                GOLD_BORDER,
              backgroundColor:
                "rgba(251,247,242,.55)",
            }}
          >
            ← Back to Orders
          </button>
        </div>

        {/* ======================================================
            CUSTOMER + SHIPPING
            ====================================================== */}

        <div
          className="
            grid
            gap-3
            lg:grid-cols-[1.35fr_.85fr]
          "
        >
          {/* CUSTOMER */}

          <article
            className="
              rounded-[20px]
              border
              p-5
              sm:p-6
            "
            style={{
              backgroundColor: CREAM,
              borderColor: GOLD_BORDER,
            }}
          >
            <div
              className="
                mb-5
                flex
                items-center
                justify-between
                gap-4
                border-b
                pb-4
              "
              style={{
                borderColor: BORDER,
              }}
            >
              <div>
                <p
                  className="
                    text-[9px]
                    font-semibold
                    uppercase
                    tracking-[.2em]
                  "
                  style={{
                    color: GOLD,
                  }}
                >
                  Customer
                </p>

                <h2
                  className="
                    mt-1
                    font-serif
                    text-[22px]
                    font-medium
                  "
                  style={{
                    color: MAROON,
                  }}
                >
                  Customer Information
                </h2>
              </div>

              <div
                className="
                  flex
                  h-10
                  w-10
                  items-center
                  justify-center
                  rounded-full
                "
                style={{
                  color: GOLD,
                  backgroundColor:
                    MAROON,
                }}
              >
                ♙
              </div>
            </div>

            <div
              className="
                flex
                flex-col
                gap-5
                sm:flex-row
                sm:items-end
                sm:justify-between
              "
            >
              <div>
                <p
                  className="
                    font-serif
                    text-[24px]
                    font-medium
                  "
                  style={{
                    color: MAROON,
                  }}
                >
                  {customerDisplayName}
                </p>

                <p
                  className="
                    mt-1
                    text-[11px]
                  "
                  style={{
                    color: TEXT_LIGHT,
                  }}
                >
                  User ID:{" "}
                  {orderInfo.user_id}
                </p>

                <div
                  className="
                    mt-4
                    flex
                    items-center
                    gap-2
                    text-[13px]
                  "
                  style={{
                    color: TEXT_SOFT,
                  }}
                >
                  <span
                    style={{
                      color: GOLD,
                    }}
                  >
                    ☎
                  </span>

                  {customerDisplayPhone}
                </div>
              </div>

              <div
                className="
                  border-t
                  pt-4
                  sm:border-l
                  sm:border-t-0
                  sm:pl-7
                  sm:pt-0
                  sm:text-right
                "
                style={{
                  borderColor: BORDER,
                }}
              >
                <p
                  className="text-[10px]"
                  style={{
                    color: TEXT_LIGHT,
                  }}
                >
                  Final Amount
                </p>

                <p
                  className="
                    mt-1
                    font-serif
                    text-[30px]
                    font-semibold
                  "
                  style={{
                    color: MAROON,
                  }}
                >
                  {formatCurrency(
                    finalAmount
                  )}
                </p>

                {hasCoupon && (
                  <p
                    className="
                      mt-1
                      text-[10px]
                      font-medium
                    "
                    style={{
                      color:
                        "#477B60",
                    }}
                  >
                    You saved{" "}
                    {formatCurrency(
                      priceBreakdown.discountAmount
                    )}
                  </p>
                )}
              </div>
            </div>
          </article>

          {/* SHIPPING */}

          <article
            className="
              rounded-[20px]
              border
              p-5
              sm:p-6
            "
            style={{
              backgroundColor: MAROON,
              borderColor:
                GOLD_BORDER,
            }}
          >
            <p
              className="
                text-[9px]
                font-semibold
                uppercase
                tracking-[.2em]
              "
              style={{
                color: GOLD,
              }}
            >
              Delivery
            </p>

            <h2
              className="
                mt-1
                font-serif
                text-[22px]
                font-medium
                text-white
              "
            >
              Shipping Address
            </h2>

            <div
              className="
                mt-5
                border-t
                pt-4
                text-[12px]
                leading-[1.8]
              "
              style={{
                color:
                  "rgba(255,255,255,.72)",
                borderColor:
                  "rgba(212,175,55,.24)",
              }}
            >
              {orderInfo.shipping_full_name && (
                <p
                  className="
                    font-serif
                    text-[16px]
                    text-white
                  "
                >
                  {
                    orderInfo.shipping_full_name
                  }
                </p>
              )}

              <p className="mt-1">
                {orderInfo.address_line1}

                {orderInfo.address_line2
                  ? `, ${orderInfo.address_line2}`
                  : ""}
              </p>

              {orderInfo.landmark && (
                <p>
                  Landmark:{" "}
                  {orderInfo.landmark}
                </p>
              )}

              <p>
                {orderInfo.city
                  ? `${orderInfo.city}, `
                  : ""}

                {orderInfo.state || ""}

                {orderInfo.pincode
                  ? ` - ${orderInfo.pincode}`
                  : ""}
              </p>

              <p
                className="mt-2"
                style={{
                  color: GOLD,
                }}
              >
                {
                  orderInfo.shipping_phone ||
                  orderInfo.phone ||
                  "N/A"
                }
              </p>
            </div>
          </article>
        </div>

        {/* ======================================================
            COUPON
            ====================================================== */}

        {hasCoupon && (
          <div
            className="
              mt-3
              flex
              flex-col
              gap-4
              rounded-[18px]
              border
              p-4
              sm:flex-row
              sm:items-center
              sm:justify-between
              sm:px-5
            "
            style={{
              backgroundColor:
                "rgba(212,175,55,.09)",
              borderColor:
                GOLD_BORDER,
            }}
          >
            <div>
              <p
                className="
                  text-[9px]
                  font-semibold
                  uppercase
                  tracking-[.18em]
                "
                style={{
                  color: GOLD,
                }}
              >
                Special Offer
              </p>

              <p
                className="
                  mt-1
                  font-serif
                  text-[18px]
                  font-medium
                "
                style={{
                  color: MAROON,
                }}
              >
                Coupon Applied
              </p>

              <p
                className="text-[11px]"
                style={{
                  color: TEXT_SOFT,
                }}
              >
                {orderInfo.coupon_code &&
                orderInfo.coupon_code !==
                  "N/A"
                  ? `Code: ${orderInfo.coupon_code}`
                  : "Discount coupon used"}
              </p>
            </div>

            <div className="sm:text-right">
              <p
                className="text-[10px]"
                style={{
                  color: TEXT_LIGHT,
                }}
              >
                Discount
              </p>

              <p
                className="
                  font-serif
                  text-[22px]
                  font-semibold
                "
                style={{
                  color: MAROON,
                }}
              >
                -
                {formatCurrency(
                  priceBreakdown.discountAmount
                )}
              </p>
            </div>
          </div>
        )}

        {/* ======================================================
            PRICE SUMMARY
            ====================================================== */}

        <section
          className="
            mt-3
            rounded-[20px]
            border
            p-4
            sm:p-6
          "
          style={{
            backgroundColor: CREAM,
            borderColor: GOLD_BORDER,
          }}
        >
          <div
            className="
              mb-5
              border-b
              pb-4
            "
            style={{
              borderColor: BORDER,
            }}
          >
            <p
              className="
                text-[9px]
                font-semibold
                uppercase
                tracking-[.2em]
              "
              style={{
                color: GOLD,
              }}
            >
              Payment
            </p>

            <h2
              className="
                mt-1
                font-serif
                text-[23px]
                font-medium
              "
              style={{
                color: MAROON,
              }}
            >
              Price Summary
            </h2>
          </div>

          <div
            className="
              grid
              grid-cols-2
              gap-2
              sm:grid-cols-3
              lg:grid-cols-6
            "
          >
            <SummaryCard
              label="Subtotal"
              value={formatCurrency(
                priceBreakdown.subtotal
              )}
            />

            {hasCoupon && (
              <SummaryCard
                label="Discount"
                value={`-${formatCurrency(
                  priceBreakdown.discountAmount
                )}`}
              />
            )}

            <SummaryCard
              label="CGST"
              value={formatCurrency(
                priceBreakdown.totalCgst
              )}
            />

            <SummaryCard
              label="SGST"
              value={formatCurrency(
                priceBreakdown.totalSgst
              )}
            />

            <SummaryCard
              label="IGST"
              value={formatCurrency(
                priceBreakdown.totalIgst
              )}
            />

            <SummaryCard
              label="Final Amount"
              value={formatCurrency(
                finalAmount
              )}
              highlight
            />
          </div>

          {/* PRICE BREAKDOWN DESKTOP */}

          <div
            className="
              mt-6
              hidden
              overflow-x-auto
              rounded-[14px]
              border
              md:block
            "
            style={{
              borderColor: BORDER,
            }}
          >
            <table className="min-w-[1050px] w-full">
              <thead>
                <tr
                  style={{
                    backgroundColor:
                      MAROON,
                  }}
                >
                  <TableHeading>
                    Product
                  </TableHeading>

                  <TableHeading center>
                    Size
                  </TableHeading>

                  <TableHeading right>
                    Qty
                  </TableHeading>

                  <TableHeading right>
                    Price
                  </TableHeading>

                  {hasBulkDiscount && (
                    <TableHeading right>
                      Original
                    </TableHeading>
                  )}

                  {hasBulkDiscount && (
                    <TableHeading center>
                      Type
                    </TableHeading>
                  )}

                  {hasBulkDiscount && (
                    <TableHeading right>
                      Savings
                    </TableHeading>
                  )}

                  <TableHeading right>
                    Subtotal
                  </TableHeading>

                  <TableHeading right>
                    Discount
                  </TableHeading>

                  <TableHeading right>
                    CGST
                  </TableHeading>

                  <TableHeading right>
                    SGST
                  </TableHeading>

                  <TableHeading right>
                    IGST
                  </TableHeading>

                  <TableHeading right>
                    Final
                  </TableHeading>
                </tr>
              </thead>

              <tbody>
                {priceBreakdown.itemWiseBreakdown.map(
                  (item, index) => (
                    <tr
                      key={`${item.product_id}-${index}`}
                      className="border-t"
                      style={{
                        borderColor:
                          BORDER,
                      }}
                    >
                      <TableCell>
                        {item.name}
                      </TableCell>

                      <TableCell center>
                        {item.size}
                      </TableCell>

                      <TableCell right>
                        {item.quantity}
                      </TableCell>

                      <TableCell right>
                        {formatCurrency(
                          item.paidPrice
                        )}
                      </TableCell>

                      {hasBulkDiscount && (
                        <TableCell right>
                          {item.isBulkDiscount
                            ? formatCurrency(
                                item.originalPrice
                              )
                            : "-"}
                        </TableCell>
                      )}

                      {hasBulkDiscount && (
                        <TableCell center>
                          {item.isBulkDiscount ? (
                            <span
                              className="
                                rounded-full
                                px-2
                                py-1
                                text-[9px]
                                font-semibold
                              "
                              style={{
                                color:
                                  GOLD,
                                backgroundColor:
                                  MAROON,
                              }}
                            >
                              Bulk
                            </span>
                          ) : (
                            "-"
                          )}
                        </TableCell>
                      )}

                      {hasBulkDiscount && (
                        <TableCell right>
                          {item.isBulkDiscount
                            ? `-${formatCurrency(
                                item.savings
                              )}`
                            : "-"}
                        </TableCell>
                      )}

                      <TableCell right>
                        {formatCurrency(
                          item.itemSubtotal
                        )}
                      </TableCell>

                      <TableCell right>
                        -
                        {formatCurrency(
                          item.discountShare
                        )}
                      </TableCell>

                      <TableCell right>
                        {formatCurrency(
                          item.cgst
                        )}
                      </TableCell>

                      <TableCell right>
                        {formatCurrency(
                          item.sgst
                        )}
                      </TableCell>

                      <TableCell right>
                        {formatCurrency(
                          item.igst
                        )}
                      </TableCell>

                      <TableCell
                        right
                        strong
                      >
                        {formatCurrency(
                          item.finalLineTotal
                        )}
                      </TableCell>
                    </tr>
                  )
                )}
              </tbody>
            </table>
          </div>

          {/* MOBILE PRICE BREAKDOWN */}

          <div
            className="
              mt-5
              space-y-2
              md:hidden
            "
          >
            {priceBreakdown.itemWiseBreakdown.map(
              (item, index) => (
                <div
                  key={`${item.product_id}-${index}`}
                  className="
                    rounded-[14px]
                    border
                    p-4
                  "
                  style={{
                    borderColor:
                      BORDER,
                    backgroundColor:
                      SOFT_CREAM,
                  }}
                >
                  <div
                    className="
                      flex
                      items-center
                      justify-between
                      gap-3
                    "
                  >
                    <div>
                      <p
                        className="
                          font-serif
                          text-[16px]
                          font-medium
                        "
                        style={{
                          color:
                            MAROON,
                        }}
                      >
                        {item.name}
                      </p>

                      <p
                        className="
                          mt-1
                          text-[10px]
                        "
                        style={{
                          color:
                            TEXT_LIGHT,
                        }}
                      >
                        Size {item.size} ·
                        Qty{" "}
                        {item.quantity}
                      </p>
                    </div>

                    <p
                      className="
                        font-serif
                        text-[17px]
                        font-semibold
                      "
                      style={{
                        color: MAROON,
                      }}
                    >
                      {formatCurrency(
                        item.finalLineTotal
                      )}
                    </p>
                  </div>

                  <div
                    className="
                      mt-3
                      grid
                      grid-cols-2
                      gap-2
                      border-t
                      pt-3
                      text-[10px]
                    "
                    style={{
                      borderColor:
                        BORDER,
                      color: TEXT_SOFT,
                    }}
                  >
                    <span>
                      Price:{" "}
                      {formatCurrency(
                        item.paidPrice
                      )}
                    </span>

                    <span>
                      Tax:{" "}
                      {formatCurrency(
                        item.cgst +
                          item.sgst +
                          item.igst
                      )}
                    </span>

                    {item.isBulkDiscount && (
                      <>
                        <span>
                          Original:{" "}
                          {formatCurrency(
                            item.originalPrice
                          )}
                        </span>

                        <span
                          style={{
                            color:
                              "#477B60",
                          }}
                        >
                          Saved:{" "}
                          {formatCurrency(
                            item.savings
                          )}
                        </span>
                      </>
                    )}
                  </div>
                </div>
              )
            )}
          </div>
        </section>

        {/* ======================================================
            PRODUCTS ORDERED
            ====================================================== */}

        <section
          className="
            mt-3
            rounded-[20px]
            border
            p-4
            sm:p-6
          "
          style={{
            backgroundColor: CREAM,
            borderColor: GOLD_BORDER,
          }}
        >
          <div
            className="
              flex
              items-end
              justify-between
              gap-4
              border-b
              pb-4
            "
            style={{
              borderColor: BORDER,
            }}
          >
            <div>
              <p
                className="
                  text-[9px]
                  font-semibold
                  uppercase
                  tracking-[.2em]
                "
                style={{
                  color: GOLD,
                }}
              >
                Your Selection
              </p>

              <h2
                className="
                  mt-1
                  font-serif
                  text-[23px]
                  font-medium
                "
                style={{
                  color: MAROON,
                }}
              >
                Products Ordered
              </h2>
            </div>

            <span
              className="
                text-[10px]
                uppercase
                tracking-[.12em]
              "
              style={{
                color: TEXT_LIGHT,
              }}
            >
              {enhancedProducts.length}{" "}
              {enhancedProducts.length ===
              1
                ? "Item"
                : "Items"}
            </span>
          </div>

          <div
            className="
              mt-4
              grid
              gap-3
              md:grid-cols-2
              xl:grid-cols-3
            "
          >
            {priceBreakdown.itemWiseBreakdown.map(
              (item) => {
                const lineTax =
                  item.cgst +
                  item.sgst +
                  item.igst;

                return (
                  <article
                    key={
                      item.product_id
                    }
                    className="
                      overflow-hidden
                      rounded-[16px]
                      border
                    "
                    style={{
                      borderColor:
                        BORDER,
                      backgroundColor:
                        SOFT_CREAM,
                    }}
                  >
                    <div className="flex gap-4 p-4">
                      <div
                        className="
                          h-[95px]
                          w-[82px]
                          flex-shrink-0
                          overflow-hidden
                          rounded-[12px]
                          border
                          p-1
                        "
                        style={{
                          borderColor:
                            GOLD_BORDER,
                          backgroundColor:
                            CREAM,
                        }}
                      >
                        <img
                          src={getImageUrl(
                            item.img_path
                          )}
                          alt={item.name}
                          className="
                            h-full
                            w-full
                            object-contain
                          "
                          onError={(e) => {
                            e.target.onerror =
                              null;

                            e.target.src =
                              FALLBACK_IMG;
                          }}
                          loading="lazy"
                        />
                      </div>

                      <div className="min-w-0 flex-1">
                        <p
                          className="
                            text-[9px]
                            uppercase
                            tracking-[.14em]
                          "
                          style={{
                            color:
                              TEXT_LIGHT,
                          }}
                        >
                          Product #
                          {item.product_id}
                        </p>

                        <h3
                          className="
                            mt-1
                            font-serif
                            text-[18px]
                            font-medium
                            leading-tight
                          "
                          style={{
                            color:
                              MAROON,
                          }}
                        >
                          {item.name}
                        </h3>

                        <p
                          className="
                            mt-2
                            text-[10px]
                          "
                          style={{
                            color:
                              TEXT_SOFT,
                          }}
                        >
                          Size {item.size} ·
                          Qty{" "}
                          {item.quantity}
                        </p>

                        {item.isBulkDiscount && (
                          <span
                            className="
                              mt-2
                              inline-flex
                              rounded-full
                              px-2
                              py-1
                              text-[8px]
                              font-semibold
                              uppercase
                              tracking-[.1em]
                            "
                            style={{
                              color: GOLD,
                              backgroundColor:
                                MAROON,
                            }}
                          >
                            Bulk Discount
                          </span>
                        )}
                      </div>
                    </div>

                    <div
                      className="
                        grid
                        grid-cols-3
                        border-t
                      "
                      style={{
                        borderColor:
                          BORDER,
                      }}
                    >
                      <ProductStat
                        label="Price"
                        value={formatCurrency(
                          item.paidPrice
                        )}
                      />

                      <ProductStat
                        label="Tax"
                        value={formatCurrency(
                          lineTax
                        )}
                      />

                      <ProductStat
                        label="Final"
                        value={formatCurrency(
                          item.finalLineTotal
                        )}
                        strong
                      />
                    </div>
                  </article>
                );
              }
            )}
          </div>
        </section>

        {/* ======================================================
            FINAL TOTAL
            ====================================================== */}

        <div
          className="
            mt-3
            rounded-[20px]
            border
            p-5
            sm:p-6
          "
          style={{
            color: "#fff",
            backgroundColor: MAROON,
            borderColor: GOLD_BORDER,
          }}
        >
          <div
            className="
              flex
              flex-col
              gap-5
              sm:flex-row
              sm:items-center
              sm:justify-between
            "
          >
            <div>
              <p
                className="
                  text-[9px]
                  font-semibold
                  uppercase
                  tracking-[.22em]
                "
                style={{
                  color: GOLD,
                }}
              >
                Order Total
              </p>

              <h3
                className="
                  mt-1
                  font-serif
                  text-[24px]
                  font-medium
                "
              >
                Thank you for choosing
                SL Gold.
              </h3>

              <p
                className="
                  mt-1
                  text-[11px]
                "
                style={{
                  color:
                    "rgba(255,255,255,.60)",
                }}
              >
                Order #
                {orderInfo.order_id}
              </p>
            </div>

            <div className="sm:text-right">
              <p
                className="text-[10px]"
                style={{
                  color:
                    "rgba(255,255,255,.58)",
                }}
              >
                Final Amount
              </p>

              <p
                className="
                  mt-1
                  font-serif
                  text-[34px]
                  font-semibold
                "
                style={{
                  color: GOLD,
                }}
              >
                {formatCurrency(
                  finalAmount
                )}
              </p>
            </div>
          </div>
        </div>

        <button
          onClick={() =>
            navigate("/order-section")
          }
          className="
            mt-4
            w-full
            rounded-full
            border
            py-3
            text-[10px]
            font-semibold
            uppercase
            tracking-[.14em]
            sm:hidden
          "
          style={{
            color: GOLD,
            backgroundColor: MAROON,
            borderColor: MAROON,
          }}
        >
          ← Back to Orders
        </button>
      </div>
    </section>
  );
};

/* ============================================================
   SMALL UI COMPONENTS
   ============================================================ */

const SummaryCard = ({
  label,
  value,
  highlight = false,
}) => {
  return (
    <div
      className="
        rounded-[14px]
        border
        p-3
        sm:p-4
      "
      style={{
        backgroundColor: highlight
          ? MAROON
          : SOFT_CREAM,

        borderColor: highlight
          ? GOLD_BORDER
          : BORDER,
      }}
    >
      <p
        className="
          text-[9px]
          uppercase
          tracking-[.12em]
        "
        style={{
          color: highlight
            ? "rgba(255,255,255,.58)"
            : TEXT_LIGHT,
        }}
      >
        {label}
      </p>

      <p
        className="
          mt-1
          font-serif
          text-[16px]
          font-semibold
          sm:text-[17px]
        "
        style={{
          color: highlight
            ? GOLD
            : MAROON,
        }}
      >
        {value}
      </p>
    </div>
  );
};

const TableHeading = ({
  children,
  center = false,
  right = false,
}) => {
  return (
    <th
      className={`
        px-3
        py-3
        text-[9px]
        font-semibold
        uppercase
        tracking-[.12em]

        ${
          center
            ? "text-center"
            : right
            ? "text-right"
            : "text-left"
        }
      `}
      style={{
        color: GOLD,
      }}
    >
      {children}
    </th>
  );
};

const TableCell = ({
  children,
  center = false,
  right = false,
  strong = false,
}) => {
  return (
    <td
      className={`
        whitespace-nowrap
        px-3
        py-3
        text-[11px]

        ${
          center
            ? "text-center"
            : right
            ? "text-right"
            : "text-left"
        }

        ${
          strong
            ? "font-semibold"
            : ""
        }
      `}
      style={{
        color: strong
          ? MAROON
          : TEXT_SOFT,
      }}
    >
      {children}
    </td>
  );
};

const ProductStat = ({
  label,
  value,
  strong = false,
}) => {
  return (
    <div
      className="
        border-r
        p-3
        last:border-r-0
      "
      style={{
        borderColor: BORDER,
      }}
    >
      <p
        className="
          text-[8px]
          uppercase
          tracking-[.1em]
        "
        style={{
          color: TEXT_LIGHT,
        }}
      >
        {label}
      </p>

      <p
        className="
          mt-1
          font-serif
          text-[12px]
          font-semibold
        "
        style={{
          color: strong
            ? MAROON
            : TEXT_SOFT,
        }}
      >
        {value}
      </p>
    </div>
  );
};

export default ViewDetails;